import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withStyles} from '@material-ui/core/styles';
import DashboardTemplate from "../../Common/DashboardTemplate";
import {
    Checkbox,
    CircularProgress,
    TablePagination,
    Typography
} from "@material-ui/core";
import axios from 'axios';
import TextField from "@material-ui/core/TextField";
import {Button} from "@material-ui/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTrash} from "@fortawesome/free-solid-svg-icons";
import DrawerMenuRight from "../../Common/DrawerMenuRight";
import { AuthenticationService } from "../../../Services/AuthenticationService";
import SortableTableHeader from '../../Common/SortableTableHeader';
import { bindTableSortHandler, sortCollection, USER_SORT_ACCESSORS } from '../../../utils/tableSortUtils';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

const apiBaseUrl = process.env.REACT_APP_API_URL;

const styles = theme => ({
    drawerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    checkboxGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '16px',
    },
    checkboxItem: {
        display: 'flex',
        alignItems: 'center',
    },
});

class UserManagement extends React.Component {
    state = {
        loading: true,
        users: [],
        administrators: [],
        nonAdministrators: [],
        editingUser: null,
        isCreatingUser: false,
        creatingAdminUser: false,
        editDrawerOpen: false,
        editFirstName: '',
        editLastName: '',
        editEmail: '',
        editPassword: '',
        editPermissions: {
            EditAssembliesAndParts: false,
            EditPermissions: false,
            Purchase: false,
            Workshop: false,
        },
        hasUnsavedChanges: false,
        saving: false,
        adminPage: 0,
        adminRowsPerPage: 25,
        nonAdminPage: 0,
        nonAdminRowsPerPage: 25,
        adminUserSortField: 'name',
        adminUserSortDirection: 'asc',
        nonAdminUserSortField: 'name',
        nonAdminUserSortDirection: 'asc',
    };

    constructor(props) {
        super(props);
        const jwtToken = AuthenticationService.getInstance().getToken();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + jwtToken;
    }

    componentDidMount() {
        this.loadUsers();
    }

    loadUsers = async () => {
        try {
            this.setState({loading: true});
            const response = await axios.get(`${apiBaseUrl}users`);
            const users = response.data;
            
            const administrators = users.filter(u => u.isAdministrator);
            const nonAdministrators = users.filter(u => !u.isAdministrator);
            
            this.setState({
                users,
                administrators,
                nonAdministrators,
                loading: false,
            });
        } catch (error) {
            console.error('Error loading users:', error);
            window.$alertError('Failed to load users');
            this.setState({loading: false});
        }
    };

    handleCreateUser = (isAdmin) => {
        this.setState({
            editingUser: null,
            isCreatingUser: true,
            creatingAdminUser: isAdmin,
            editDrawerOpen: true,
            editFirstName: '',
            editLastName: '',
            editEmail: '',
            editPassword: '',
            editPermissions: {
                EditAssembliesAndParts: false,
                EditPermissions: false,
                Purchase: false,
                Workshop: false,
            },
            hasUnsavedChanges: false,
        });
    };

    handleUserClick = (user) => {
        const permissions = user.userPermissions || [];
        // Handle both array of strings and array of objects with permissionType property
        const permissionTypes = permissions.map(p => typeof p === 'string' ? p : p.permissionType);
        const permissionMap = {
            EditAssembliesAndParts: permissionTypes.includes('EditAssembliesAndParts'),
            EditPermissions: permissionTypes.includes('EditPermissions'),
            Purchase: permissionTypes.includes('Purchase'),
            Workshop: permissionTypes.includes('Workshop'),
        };

        this.setState({
            editingUser: user,
            isCreatingUser: false,
            creatingAdminUser: false,
            editDrawerOpen: true,
            editFirstName: user.firstName || '',
            editLastName: user.lastName || '',
            editEmail: user.email || '',
            editPassword: '',
            editPermissions: permissionMap,
            hasUnsavedChanges: false,
        });
    };

    handleCloseDrawer = () => {
        this.setState({
            editDrawerOpen: false,
            editingUser: null,
            isCreatingUser: false,
            creatingAdminUser: false,
            hasUnsavedChanges: false,
        });
    };

    handleFieldChange = (field, value) => {
        this.setState({
            [field]: value,
            hasUnsavedChanges: true,
        });
    };

    handlePermissionChange = (permission, checked) => {
        this.setState(prevState => ({
            editPermissions: {
                ...prevState.editPermissions,
                [permission]: checked,
            },
            hasUnsavedChanges: true,
        }));
    };

    handleDeleteUser = async (user, event) => {
        event.stopPropagation(); // Prevent opening the edit drawer
        
        if (!window.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`)) {
            return;
        }

        try {
            await axios.delete(`${apiBaseUrl}users/${user.id}`);
            window.$alertSuccess('User deleted successfully');
            await this.loadUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete user';
            window.$alertError(errorMessage);
        }
    };

    handleSave = async () => {
        const {editingUser, isCreatingUser, creatingAdminUser, editFirstName, editLastName, editEmail, editPassword, editPermissions} = this.state;
        
        if (!editFirstName || !editLastName || !editEmail) {
            window.$alertWarning('Please fill in all required fields');
            return;
        }

        // Password is required when creating a new user
        if (isCreatingUser && (!editPassword || editPassword.length < 6)) {
            window.$alertWarning('Password is required and must be at least 6 characters long');
            return;
        }

        this.setState({saving: true});

        try {
            // Extract permissions list
            const permissionsList = Object.keys(editPermissions)
                .filter(key => editPermissions[key])
                .map(key => {
                    // Map display names back to permission types
                    const permissionMap = {
                        'EditAssembliesAndParts': 'EditAssembliesAndParts',
                        'EditPermissions': 'EditPermissions',
                        'Purchase': 'Purchase',
                        'Workshop': 'Workshop',
                    };
                    return permissionMap[key] || key;
                });

            if (isCreatingUser) {
                // Create new user
                const endpoint = creatingAdminUser ? `${apiBaseUrl}users/admin` : `${apiBaseUrl}users/non-admin`;
                await axios.post(endpoint, {
                    firstName: editFirstName,
                    lastName: editLastName,
                    email: editEmail,
                    password: editPassword,
                    userPermissions: permissionsList,
                });

                window.$alertSuccess(`User created successfully`);
            } else {
                // Update existing user
                await axios.put(`${apiBaseUrl}users/${editingUser.id}`, {
                    firstName: editFirstName,
                    lastName: editLastName,
                    email: editEmail,
                    userPermissions: permissionsList,
                });

                // Update password if provided
                if (editPassword && editPassword.length > 0) {
                    if (editPassword.length < 6) {
                        window.$alertWarning('Password must be at least 6 characters long');
                        this.setState({saving: false});
                        return;
                    }
                    await axios.put(`${apiBaseUrl}users/${editingUser.id}/password`, {
                        newPassword: editPassword,
                    });
                }

                window.$alertSuccess('User updated successfully');
            }

            await this.loadUsers();
            this.setState({
                editDrawerOpen: false,
                editingUser: null,
                isCreatingUser: false,
                creatingAdminUser: false,
                hasUnsavedChanges: false,
                saving: false,
            });
        } catch (error) {
            console.error('Error saving user:', error);
            const errorMessage = error.response?.data?.message || error.message || (isCreatingUser ? 'Failed to create user' : 'Failed to update user');
            window.$alertError(errorMessage);
            this.setState({saving: false});
        }
    };

    getPermissionDisplayName = (permissionType) => {
        const displayNames = {
            'EditAssembliesAndParts': 'Edit Parts',
            'EditPermissions': 'Edit Permissions',
            'Purchase': 'Purchase',
            'Workshop': 'Workshop Tools',
        };
        return displayNames[permissionType] || permissionType;
    };

    handleAdminUserSort = bindTableSortHandler(this, {
        fieldKey: 'adminUserSortField',
        directionKey: 'adminUserSortDirection',
    });

    handleNonAdminUserSort = bindTableSortHandler(this, {
        fieldKey: 'nonAdminUserSortField',
        directionKey: 'nonAdminUserSortDirection',
    });

    renderUserTable = (users, title, isAdmin) => {
        const pageKey = isAdmin ? 'adminPage' : 'nonAdminPage';
        const rowsPerPageKey = isAdmin ? 'adminRowsPerPage' : 'nonAdminRowsPerPage';
        const sortFieldKey = isAdmin ? 'adminUserSortField' : 'nonAdminUserSortField';
        const sortDirectionKey = isAdmin ? 'adminUserSortDirection' : 'nonAdminUserSortDirection';
        const handleSort = isAdmin ? this.handleAdminUserSort : this.handleNonAdminUserSort;
        const page = this.state[pageKey];
        const rowsPerPage = this.state[rowsPerPageKey];
        const sortField = this.state[sortFieldKey];
        const sortDirection = this.state[sortDirectionKey];

        const sortedUsers = sortCollection(users, sortField, sortDirection, USER_SORT_ACCESSORS);
        
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

        return (
            <div className="container" style={{maxWidth: 1800, margin: '0 auto 20px'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px'}}>
                    <Typography variant="h6" style={{fontWeight: 'bold'}}>
                        {title}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => this.handleCreateUser(isAdmin)}
                        className="success-button-lg"
                    >
                        + Create {isAdmin ? 'Administrator' : 'Non-Administrator'}
                    </Button>
                </div>
                <center>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        colSpan={4}
                        count={sortedUsers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        slotProps={{
                            select: {
                                'aria-label': 'rows per page',
                            },
                            actions: {
                                showFirstButton: true,
                                showLastButton: true,
                                slots: {
                                    firstPageIcon: FirstPageIcon,
                                    lastPageIcon: LastPageIcon,
                                    nextPageIcon: ChevronRightIcon,
                                    backPageIcon: ChevronLeftIcon,
                                },
                            },
                        }}
                        onPageChange={(event, newPage) => this.setState({[pageKey]: newPage})}
                        onRowsPerPageChange={(event) => this.setState({
                            [rowsPerPageKey]: parseInt(event.target.value, 10),
                            [pageKey]: 0,
                        })}
                    />
                </center>
                <div className="parts-table-container">
                    <table className="parts-table">
                        <thead>
                            <tr>
                                <SortableTableHeader field="name" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} style={{minWidth: 200}}>Name</SortableTableHeader>
                                <SortableTableHeader field="email" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} style={{minWidth: 250}}>Email</SortableTableHeader>
                                <SortableTableHeader field="permissions" sortField={sortField} sortDirection={sortDirection} onSort={handleSort} style={{minWidth: 400}}>Permissions</SortableTableHeader>
                                <th style={{minWidth: 80, textAlign: 'center'}}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedUsers.length > 0 ? (
                                paginatedUsers.map((user) => (
                                    <tr
                                        key={user.id}
                                        onClick={() => this.handleUserClick(user)}
                                        style={{cursor: 'pointer'}}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                                    >
                                        <td style={{textAlign: 'left', padding: '8px'}}>
                                            {user.firstName} {user.lastName}
                                        </td>
                                        <td style={{textAlign: 'left', padding: '8px'}}>{user.email}</td>
                                        <td style={{textAlign: 'left', padding: '8px'}}>
                                            <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                                                {user.userPermissions && user.userPermissions.length > 0 ? (
                                                    // Extract permission types and sort by display name alphabetically
                                                    user.userPermissions
                                                        .map(p => typeof p === 'string' ? p : p.permissionType)
                                                        .sort((a, b) => {
                                                            const displayA = this.getPermissionDisplayName(a);
                                                            const displayB = this.getPermissionDisplayName(b);
                                                            return displayA.localeCompare(displayB);
                                                        })
                                                        .map((permissionType, index) => (
                                                            <span 
                                                                key={index} 
                                                                style={{
                                                                    display: 'inline-block',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    backgroundColor: '#e3f2fd',
                                                                    color: '#1976d2',
                                                                    fontSize: '12px',
                                                                    margin: '2px',
                                                                }}
                                                            >
                                                                {this.getPermissionDisplayName(permissionType)}
                                                            </span>
                                                        ))
                                                ) : (
                                                    <span style={{color: '#999', fontStyle: 'italic'}}>No permissions</span>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{textAlign: 'center', padding: '8px'}} onClick={(e) => e.stopPropagation()}>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                size="small"
                                                onClick={(e) => this.handleDeleteUser(user, e)}
                                                style={{minWidth: 'auto', padding: '4px 8px'}}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" style={{textAlign: 'center', padding: '20px', color: '#999'}}>
                                        No users found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    render() {
        const {classes} = this.props;
        const {
            loading,
            administrators,
            nonAdministrators,
            editDrawerOpen,
            isCreatingUser,
            creatingAdminUser,
            editingUser,
            editFirstName,
            editLastName,
            editEmail,
            editPassword,
            editPermissions,
            hasUnsavedChanges,
            saving,
        } = this.state;

        const user = AuthenticationService.getInstance().getUser();
        const permissions = user ? user.userPermissions : [];
        
        if (!permissions.includes('EditPermissions')) {
            return (
                <DashboardTemplate title="User Management" onSyncDatabase={this.loadUsers}>
                    <div style={{padding: '20px', textAlign: 'center'}}>
                        <Typography variant="h6" color="error">
                            You do not have permission to access this page.
                        </Typography>
                    </div>
                </DashboardTemplate>
            );
        }

        if (loading) {
            return (
                <DashboardTemplate title="User Management" onSyncDatabase={this.loadUsers}>
                    <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px'}}>
                        <CircularProgress />
                    </div>
                </DashboardTemplate>
            );
        }

        return (
            <DashboardTemplate title="User Management" onSyncDatabase={this.loadUsers}>
                <div className="main-dashboard-root">
                    {this.renderUserTable(administrators, 'Administrators', true)}
                    {this.renderUserTable(nonAdministrators, 'Non-Administrators', false)}

                    <DrawerMenuRight
                        open={editDrawerOpen}
                        onClose={this.handleCloseDrawer}
                        title={isCreatingUser ? "Create User" : "Edit User"}
                        hasUnsavedChanges={hasUnsavedChanges}
                    >
                        <div className={classes.drawerContent}>
                            <div className={classes.formGroup}>
                                <TextField
                                    label="First Name"
                                    value={editFirstName}
                                    onChange={(e) => this.handleFieldChange('editFirstName', e.target.value)}
                                    fullWidth
                                    required
                                />
                            </div>
                            <div className={classes.formGroup}>
                                <TextField
                                    label="Last Name"
                                    value={editLastName}
                                    onChange={(e) => this.handleFieldChange('editLastName', e.target.value)}
                                    fullWidth
                                    required
                                />
                            </div>
                            <div className={classes.formGroup}>
                                <TextField
                                    label="Email"
                                    value={editEmail}
                                    onChange={(e) => this.handleFieldChange('editEmail', e.target.value)}
                                    fullWidth
                                    required
                                    type="email"
                                />
                            </div>
                            <div className={classes.formGroup}>
                                <TextField
                                    label={isCreatingUser ? "Password" : "New Password (leave blank to keep current)"}
                                    value={editPassword}
                                    onChange={(e) => this.handleFieldChange('editPassword', e.target.value)}
                                    fullWidth
                                    type="password"
                                    required={isCreatingUser}
                                />
                            </div>
                            {/* Only show permissions checkboxes when editing existing user or creating administrator */}
                            {(!isCreatingUser || (isCreatingUser && creatingAdminUser)) && (
                                <div className={classes.checkboxGroup}>
                                    <Typography variant="subtitle1" style={{fontWeight: 'bold', marginBottom: '8px'}}>
                                        Permissions
                                    </Typography>
                                    <div className={classes.checkboxItem}>
                                        <Checkbox
                                            checked={editPermissions.EditAssembliesAndParts}
                                            onChange={(e) => this.handlePermissionChange('EditAssembliesAndParts', e.target.checked)}
                                            color="primary"
                                        />
                                        <Typography>Edit Parts</Typography>
                                    </div>
                                    <div className={classes.checkboxItem}>
                                        <Checkbox
                                            checked={editPermissions.EditPermissions}
                                            onChange={(e) => this.handlePermissionChange('EditPermissions', e.target.checked)}
                                            color="primary"
                                        />
                                        <Typography>Edit Permissions</Typography>
                                    </div>
                                    <div className={classes.checkboxItem}>
                                        <Checkbox
                                            checked={editPermissions.Purchase}
                                            onChange={(e) => this.handlePermissionChange('Purchase', e.target.checked)}
                                            color="primary"
                                        />
                                        <Typography>Purchase</Typography>
                                    </div>
                                    <div className={classes.checkboxItem}>
                                        <Checkbox
                                            checked={editPermissions.Workshop}
                                            onChange={(e) => this.handlePermissionChange('Workshop', e.target.checked)}
                                            color="primary"
                                        />
                                        <Typography>Workshop Tools</Typography>
                                    </div>
                                </div>
                            )}
                            <div style={{display: 'flex', gap: '8px', marginTop: '16px'}}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={this.handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={this.handleCloseDrawer}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </DrawerMenuRight>
                </div>
            </DashboardTemplate>
        );
    }
}

UserManagement.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(UserManagement);

